import { Routes } from '@angular/router';

import { authGuard } from './app/guards/auth.guard';
import { isClientGuard, isNotClientGuard } from './app/guards/is-client-guard';
import { isBossesGuard, isManagersGuard } from './app/guards/is-managers-guard';
import { pauImportLeaveGuard } from './app/guards/pau-import-leave-guard';
import { unsavedChangesGuard } from './app/guards/unsaved-changes-guard';
import { Notfound } from './app/pages/common-pages/notfound/notfound';

export const appRoutes: Routes = [
  // Вход и смена временного пароля — вне каркаса и вне authGuard (CRM-122).
  { path: 'login', loadComponent: () => import('./app/pages/common-pages/login-page/login-page.component').then((m) => m.LoginPageComponent) },
  {
    path: 'change-password',
    loadComponent: () => import('./app/pages/common-pages/change-password-page/change-password-page.component').then((m) => m.ChangePasswordPageComponent)
  },
  {
    path: '',
    // Закрыто по умолчанию (BR-16): один guard на корень и всех детей.
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    loadComponent: () => import('./app/pages/user-pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    children: [
      {
        path: 'debtors/all',
        canActivate: [isClientGuard],
        loadComponent: () => import('./app/pages/user-pages/debtors-page/debtors-page.component').then((m) => m.DebtorsPageComponent)
      },
      {
        path: 'debtors/create',
        canActivate: [isClientGuard],
        loadComponent: () => import('./app/pages/user-pages/debtor-create-page/debtor-create-page.component').then((m) => m.DebtorCreatePageComponent)
      },
      {
        // Импорт из ПАУ (CRM-108) — до `debtors/:id`, иначе откроется карточка с id = "pau-import".
        path: 'debtors/pau-import',
        canActivate: [isClientGuard],
        canDeactivate: [pauImportLeaveGuard],
        loadComponent: () => import('./app/pages/user-pages/pau-import-page/pau-import-page.component').then((m) => m.PauImportPageComponent)
      },
      {
        // Карточка должника: вкладка — сегмент URL (`debtors/1/info`), чтобы ссылками
        // можно было делиться (CRM-6). Guard один раз на parent, компонент
        // переиспользуется при смене `:tab` — состояние форм не теряется.
        path: 'debtors/:id',
        canActivate: [isClientGuard],
        // Несохранённые правки («Иные сведения», анализ кредитного досье, Д-13): уход и смена должника; смену `:tab` спрашивает `changeTab`.
        canDeactivate: [unsavedChangesGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'info' },
          {
            path: ':tab',
            loadComponent: () => import('./app/pages/user-pages/debtor-item-page/debtor-item-page.component').then((m) => m.DebtorItemPageComponent)
          }
        ]
      },
      {
        path: 'user-profile',
        canActivate: [isClientGuard],
        loadComponent: () => import('./app/pages/user-pages/user-profile-page/user-profile-page.component').then((m) => m.UserProfilePageComponent)
      },
      {
        path: 'notifications',
        canActivate: [isClientGuard],
        loadComponent: () => import('./app/pages/user-pages/user-notifications-page/user-notifications-page.component').then((m) => m.UserNotificationsPageComponent)
      },
      {
        path: 'tasks-scheduler',
        canActivate: [isClientGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./app/components/issues/issues-page.component').then((m) => m.IssuesPageComponent)
      },
      {
        // Смена `:id` запускает guard, смена query-параметров — нет.
        path: 'tasks-scheduler/:id',
        canActivate: [isClientGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./app/pages/user-pages/task-item-page/task-item-page.component').then((m) => m.TaskItemPageComponent)
      },
      {
        path: 'law-firm-debtors',
        canActivate: [isNotClientGuard],
        loadComponent: () => import('./app/pages/client-pages/law-firm-debtors/law-firm-debtors').then((m) => m.LawFirmDebtors)
      },
      {
        path: 'law-firm-debtors/:id',
        canActivate: [isNotClientGuard],
        loadComponent: () => import('./app/pages/client-pages/client-files-page/client-files-page.component').then((m) => m.ClientFilesPageComponent)
      },
      {
        path: 'law-firm-documents',
        canActivate: [isNotClientGuard],
        loadComponent: () => import('./app/pages/client-pages/law-firm-documents/law-firm-documents').then((m) => m.LawFirmDocuments)
      },
      {
        path: 'financial-managers',
        loadComponent: () => import('./app/components/financial-manager/financial-manager').then((m) => m.FinancialManager)
      },
      {
        path: 'handbooks',
        canActivate: [isClientGuard],
        loadComponent: () => import('./app/pages/user-pages/handbooks-page/handbooks-page.component').then((m) => m.HandbooksPageComponent)
      },
      {
        // Задания «завести должника в ЕФРСБ» по ФУ (CRM-118, Э-4).
        path: 'efrsb',
        canActivate: [isClientGuard],
        loadComponent: () => import('./app/pages/user-pages/efrsb-page/efrsb-page.component').then((m) => m.EfrsbPageComponent)
      },
      {
        // Установка закладки «CRM → ЕФРСБ» (CRM-118, Э-5).
        path: 'efrsb/setup',
        canActivate: [isClientGuard],
        loadComponent: () => import('./app/pages/user-pages/efrsb-setup-page/efrsb-setup-page.component').then((m) => m.EfrsbSetupPageComponent)
      },
      {
        // Пользователи и журнал входов (CRM-122): только boss/admin.
        path: 'users',
        canActivate: [isBossesGuard],
        loadComponent: () => import('./app/pages/user-pages/users-page/users-page.component').then((m) => m.UsersPageComponent)
      },
      {
        path: 'audit',
        canActivate: [isManagersGuard],
        loadComponent: () => import('./app/pages/user-pages/audit-page/audit-page.component').then((m) => m.AuditPageComponent)
      }
    ]
  },
  { path: 'notfound', component: Notfound },
  { path: '**', redirectTo: '/notfound' }
];
